import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getFirebaseClients } from '../firebase';
import { toIsoString } from '../schemas/common';
import { parseDocs } from './parseDocs';
import {
  formationInputSchema,
  formationSchema,
  type Formation,
  type FormationInput,
} from '../schemas/formation';

const COLLECTION = 'formations';

function db() {
  return getFirebaseClients().firestore;
}

/**
 * Pre-Zod normalisation of `programme[]` so legacy documents (written before
 * the day→module rename) still parse cleanly. Old shape was
 * `{ day, title, bullets }`; new shape is
 * `{ order, title, bullets, startDate?, endDate?, formateurIds? }`.
 */
function normaliseProgramme(value: unknown): unknown {
  if (!Array.isArray(value)) return value;
  return value.map((m, i) => {
    const item = (m && typeof m === 'object' ? m : {}) as Record<string, unknown>;
    return {
      ...item,
      order: (item.order as number | undefined) ?? (item.day as number | undefined) ?? i + 1,
      formateurIds: Array.isArray(item.formateurIds) ? item.formateurIds : [],
      bullets: Array.isArray(item.bullets) ? item.bullets : [],
    };
  });
}

function parse(snapshotData: unknown, id: string): Formation {
  const data = snapshotData as Record<string, unknown>;
  return formationSchema.parse({
    ...data,
    id,
    programme: normaliseProgramme(data.programme),
    createdAt: toIsoString(data.createdAt),
    updatedAt: toIsoString(data.updatedAt),
  });
}

export async function listPublishedFormations(): Promise<Formation[]> {
  const q = query(
    collection(db(), COLLECTION),
    where('status', '==', 'published'),
    orderBy('displayOrder', 'asc'),
  );
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION);
}

export async function listAllFormations(): Promise<Formation[]> {
  const q = query(collection(db(), COLLECTION), orderBy('displayOrder', 'asc'));
  const snap = await getDocs(q);
  return parseDocs(snap.docs, parse, COLLECTION);
}

export async function getFormation(id: string): Promise<Formation | null> {
  const snap = await getDoc(doc(db(), COLLECTION, id));
  if (!snap.exists()) return null;
  return parse(snap.data(), snap.id);
}

/**
 * Resolve a PUBLISHED formation from a public URL segment.
 *
 * A formation's document ID is its ORIGINAL slug and is immutable, while the
 * `slug` field is editable. After an admin changes the slug (the public URL),
 * fetching by doc ID with the new slug hits a missing doc — and under the
 * `status == 'published'` read rule a get on a missing doc throws
 * `permission-denied` (not "not found"), which broke the detail pages.
 *
 * So resolve by the `slug` FIELD first (constrained to published so the query
 * satisfies the security rules for anonymous visitors), then fall back to the
 * doc ID for legacy/bookmarked URLs that predate a slug change. The fallback is
 * wrapped because that same missing-doc get can throw.
 */
export async function getFormationBySlug(slugOrId: string): Promise<Formation | null> {
  const q = query(
    collection(db(), COLLECTION),
    where('slug', '==', slugOrId),
    where('status', '==', 'published'),
    limit(1),
  );
  const snap = await getDocs(q);
  const first = snap.docs[0];
  if (first) return parse(first.data(), first.id);
  try {
    return await getFormation(slugOrId);
  } catch {
    return null;
  }
}

/** Create a formation. Uses the slug as the document ID. */
export async function createFormation(input: FormationInput): Promise<string> {
  const parsed = formationInputSchema.parse(input);
  const now = new Date().toISOString();
  await setDoc(doc(db(), COLLECTION, parsed.slug), {
    ...parsed,
    createdAt: now,
    updatedAt: now,
  });
  return parsed.slug;
}

export async function updateFormation(id: string, patch: Partial<FormationInput>): Promise<void> {
  await updateDoc(doc(db(), COLLECTION, id), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteFormation(id: string): Promise<void> {
  await deleteDoc(doc(db(), COLLECTION, id));
}
